// eslint-disable react/jsx-props-no-spreading
import React, { useRef } from 'react';
import classNames from 'classnames';
import PropTypes from 'prop-types';
import get from 'lodash/get';
import ReactTooltip from 'react-tooltip';
import { Droppable } from 'react-beautiful-dnd';
import { showErrorToast } from 'shared-components/BaseToast/Toast';
import {
  uploadImageToCdn,
  uploadAndConvertToPng,
} from 'services/api/admin-images';
import styles from './AdminImageUpload.module.css';

const DeleteButton = ({ onClick, style }) => (
  <button
    type="button"
    className={styles.deleteButton}
    style={style}
    onClick={onClick}
  >
    <div>
      <i className="fas fa-times" />
    </div>
  </button>
);

const UploadButton = ({ onClick, text, error, style, disabled }) => (
  <button
    type="button"
    className={classNames(styles.uploadButton, {
      [styles.error]: !!error,
      [styles.disabled]: disabled,
    })}
    onClick={onClick}
    style={style}
  >
    <i className="fal fa-arrow-circle-up" />
    <span>{text}</span>
  </button>
);

const AdminImageUpload = ({
  imageUrl,
  error,
  uploadText,
  labelText,
  updateImageUrl,
  style,
  tooltip,
  validateImage,
  disabled,
  imageType,
  name,
  removeThumbnailImage,
}) => {
  const isBanner = imageType === 'banner';

  const imageInputRef = useRef(null);

  const removeBannerImage = () => {
    updateImageUrl('');
  };

  const openFileDialog = () => {
    imageInputRef.current.click();
  };

  const uploadToCdn = async (formData) => {
    try {
      const response = await uploadImageToCdn(formData);
      const { value: cdnUrl } = response;
      updateImageUrl(cdnUrl);
    } catch {
      showErrorToast('Image Upload Failed', 'Error during uploading image.');
    }
  };

  const uploadAndConvert = async (formData) => {
    try {
      const response = await uploadAndConvertToPng(formData);
      const jpeg = get(response, 'data.items[0].originalUploadedUrls[0]', '');
      const png = get(response, 'data.items[0].convertedUploadedUrls[0]', '');
      updateImageUrl(jpeg, png);
    } catch (e) {
      const uploadError = get(e, 'error.errors[0].message', '');
      const jpeg = get(e, 'data.items[0].originalUploadedUrls[0]', '');
      const png = get(e, 'data.items[0].convertedUploadedUrls[0]', '');
      if (jpeg || png) {
        updateImageUrl(jpeg, png);
        showErrorToast('Image Upload Partially Successful', uploadError);
      } else {
        showErrorToast('Image Upload Failed', uploadError);
      }
    }
  };

  const handleFileChange = async (e) => {
    const formData = new FormData();
    const currentImage = e.target.files[0];
    if (currentImage) {
      const imageError = await validateImage(currentImage);
      if (imageError) {
        showErrorToast(imageError.title, imageError.subtitle);
      } else if (isBanner) {
        formData.append('file', currentImage);
        uploadToCdn(formData);
      } else {
        formData.append('files', currentImage);
        uploadAndConvert(formData);
      }
    }
  };

  return (
    <div className={styles.container}>
      <label className={styles.label} htmlFor={name}>
        {labelText}
        {tooltip && (
          <span data-tip={tooltip} data-for="image-upload">
            <i className="fas fa-info-circle" />
          </span>
        )}
      </label>
      <input
        ref={imageInputRef}
        type="file"
        accept="image/jpeg, image/png, image/GIF"
        className={styles.fileDialog}
        onChange={handleFileChange}
        name={name}
        disabled={disabled}
      />
      {isBanner ? (
        <div className={styles.imageContainer} style={style}>
          {imageUrl ? (
            <div>
              <img alt="thumbnail" src={imageUrl} />
              {!disabled && (
                <DeleteButton onClick={removeBannerImage} style={style} />
              )}
            </div>
          ) : (
            <UploadButton
              error={error}
              text={uploadText}
              onClick={openFileDialog}
              style={{ ...style, flexDirection: 'row' }}
              disabled={disabled}
            />
          )}
        </div>
      ) : (
        <Droppable droppableId="imageUrl">
          {(provided) => (
            <div
              {...provided.droppableProps}
              ref={provided.innerRef}
              className={styles.imageContainer}
              style={style}
            >
              {imageUrl ? (
                <div>
                  <img alt="thumbnail" src={imageUrl} />
                  {!disabled && (
                    <DeleteButton
                      onClick={removeThumbnailImage}
                      style={style}
                    />
                  )}
                </div>
              ) : (
                <UploadButton
                  error={error}
                  text={uploadText}
                  onClick={openFileDialog}
                  style={style}
                  disabled={disabled}
                />
              )}
              {provided.placeholder}
            </div>
          )}
        </Droppable>
      )}

      <ReactTooltip
        id="image-upload"
        place="top"
        type="dark"
        effect="solid"
        //eslint-disable-next-line
        html={true}
        className={styles.tooltip}
      />
    </div>
  );
};

AdminImageUpload.propTypes = {
  imageUrl: PropTypes.string,
  error: PropTypes.string,
  uploadText: PropTypes.string,
  labelText: PropTypes.string.isRequired,
  updateImageUrl: PropTypes.func.isRequired,
  style: PropTypes.shape({}),
  tooltip: PropTypes.string,
  validateImage: PropTypes.func.isRequired,
  disabled: PropTypes.bool,
  imageType: PropTypes.string,
  name: PropTypes.string,
  removeThumbnailImage: PropTypes.func,
};

AdminImageUpload.defaultProps = {
  imageUrl: '',
  error: '',
  uploadText: '',
  style: {},
  tooltip: '',
  disabled: false,
  imageType: '',
  name: '',
  removeThumbnailImage: () => {},
};

export default AdminImageUpload;
